/**
 * useKeyboardShortcuts Hook
 * Global keyboard shortcuts management system
 *
 * Features:
 * - Cross-platform support (Mac/Windows/Linux)
 * - Context-aware shortcuts
 * - Dynamic registration/unregistration
 * - Conflict prevention
 * - Help modal integration
 */

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import type { KeyboardShortcut } from '../types/shortcuts';
import { matchesShortcut } from '../types/shortcuts';

interface UseKeyboardShortcutsOptions {
  enabled?: boolean;
  context?: 'global' | 'chat' | 'editor';
  preventDefault?: boolean;
}

/**
 * Hook for registering keyboard shortcuts
 */
export const useKeyboardShortcuts = (
  shortcuts: KeyboardShortcut[],
  options: UseKeyboardShortcutsOptions = {}
) => {
  const { enabled = true, context = 'global', preventDefault = true } = options;
  const shortcutsRef = useRef<KeyboardShortcut[]>(shortcuts);

  // Update shortcuts ref when shortcuts change
  useEffect(() => {
    shortcutsRef.current = shortcuts;
  }, [shortcuts]);

  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = async (event: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in input fields
      const target = event.target as HTMLElement;
      const isInput =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable;

      // Allow certain shortcuts even in input fields (like Esc)
      const allowedInInputs = ['escape', 'esc'];
      const isAllowedInInput = allowedInInputs.includes(event.key.toLowerCase());

      if (isInput && !isAllowedInInput) {
        // Allow Cmd/Ctrl+Enter in textareas (send message)
        const isCmdEnter = (event.metaKey || event.ctrlKey) && event.key === 'Enter';
        if (!isCmdEnter) return;
      }

      // Check each shortcut
      for (const shortcut of shortcutsRef.current) {
        // Skip if context doesn't match
        if (shortcut.context && shortcut.context !== context) continue;

        // Skip if shortcut is disabled
        if (shortcut.enabled === false) continue;

        // Check if the event matches this shortcut
        if (matchesShortcut(event, shortcut.key)) {
          if (preventDefault) {
            event.preventDefault();
          }

          // Execute the action
          try {
            await shortcut.action();
          } catch (error) {
            console.error(`Error executing shortcut ${shortcut.id}:`, error);
          }

          break; // Only execute the first matching shortcut
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [enabled, context, preventDefault]);
};

/**
 * Hook for creating global shortcuts registry
 */
export const useGlobalShortcuts = (
  navigate: ReturnType<typeof useNavigate>,
  isAuthenticated: boolean,
  options: {
    onNewConversation?: () => void;
    onClearConversation?: () => void;
    onSendMessage?: () => void;
    onEditLastMessage?: () => void;
    onOpenShortcutsHelp?: () => void;
    onOpenSettings?: () => void;
    onCloseModal?: () => void;
  } = {}
): KeyboardShortcut[] => {
  const {
    onNewConversation,
    onClearConversation,
    onSendMessage,
    onEditLastMessage,
    onOpenShortcutsHelp,
    onOpenSettings,
    onCloseModal,
  } = options;

  return [
    // Navigation shortcuts
    {
      id: 'shortcut-new-conversation',
      key: 'mod+n',
      description: 'Start a new conversation',
      category: 'navigation',
      requiresAuth: true,
      action: () => {
        if (onNewConversation) {
          onNewConversation();
        } else {
          navigate('/chat?new=true');
        }
      },
      enabled: isAuthenticated,
    },
    {
      id: 'shortcut-settings',
      key: 'mod+,',
      description: 'Open settings',
      category: 'navigation',
      requiresAuth: true,
      action: () => {
        if (onOpenSettings) {
          onOpenSettings();
        } else {
          navigate('/profile');
        }
      },
      enabled: isAuthenticated,
    },

    // Action shortcuts
    {
      id: 'shortcut-clear-conversation',
      key: 'mod+l',
      description: 'Clear current conversation',
      category: 'action',
      context: 'chat',
      requiresAuth: true,
      action: () => {
        if (onClearConversation) {
          onClearConversation();
        }
      },
      enabled: isAuthenticated && !!onClearConversation,
    },
    {
      id: 'shortcut-send-message',
      key: 'mod+enter',
      description: 'Send message',
      category: 'editor',
      context: 'chat',
      requiresAuth: true,
      action: () => {
        if (onSendMessage) {
          onSendMessage();
        }
      },
      enabled: isAuthenticated && !!onSendMessage,
    },
    {
      id: 'shortcut-edit-last-message',
      key: 'arrowup',
      description: 'Edit last message',
      category: 'editor',
      context: 'chat',
      requiresAuth: true,
      action: () => {
        if (onEditLastMessage) {
          onEditLastMessage();
        }
      },
      enabled: isAuthenticated && !!onEditLastMessage,
    },

    // General shortcuts
    {
      id: 'shortcut-help',
      key: 'mod+/',
      description: 'Show keyboard shortcuts help',
      category: 'general',
      action: () => {
        if (onOpenShortcutsHelp) {
          onOpenShortcutsHelp();
        }
      },
      enabled: true,
    },
    {
      id: 'shortcut-close-modal',
      key: 'escape',
      description: 'Close modal or dialog',
      category: 'general',
      action: () => {
        if (onCloseModal) {
          onCloseModal();
        }
      },
      enabled: true,
    },
  ];
};

/**
 * Get shortcut display text based on platform
 */
export const getShortcutDisplay = (key: string): string => {
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;

  return key
    .split('+')
    .map((part) => {
      switch (part.toLowerCase()) {
        case 'mod':
          return isMac ? '⌘' : 'Ctrl';
        case 'ctrl':
          return isMac ? '⌃' : 'Ctrl';
        case 'alt':
          return isMac ? '⌥' : 'Alt';
        case 'shift':
          return isMac ? '⇧' : 'Shift';
        case 'enter':
          return '↵';
        case 'escape':
          return 'Esc';
        case 'arrowup':
          return '↑';
        case 'arrowdown':
          return '↓';
        default:
          return part.toUpperCase();
      }
    })
    .join(isMac ? '' : '+');
};
